import {Injectable, OnDestroy} from '@angular/core';

import {BehaviorSubject, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {SmagApiService} from '../api/smag-api.service';
import {Hierarchy as SmagHierarchy, HierarchyEntity as SmagHierarchyEntity} from 'smag-commons/cfg/hierarchy';
import {map, switchMap, takeUntil} from 'rxjs/operators';
import {ActivatedRoute, Router} from '@angular/router';
import {LoginService} from './login.service';
import {AuthorizedObject} from '../api/dto';

interface WithPath {
    path: string;
}

interface WithParent<T> {
    parent?: T;
}

export interface Hierarchy extends AuthorizedObject<SmagHierarchy> {
    entities?: HierarchyEntity[];
}

export interface HierarchyEntity extends AuthorizedObject<SmagHierarchyEntity>,
    WithParent<HierarchyEntity>,
    WithPath {
    children?: HierarchyEntity[];
}

interface HierarchyWithIdMap {
    hierarchy: Hierarchy;
    idMap: { [entityId: string]: HierarchyEntity };
}

/**
 * Service that deals with entity {@link Hierarchy}.
 */
@Injectable()
export class HierarchyService implements OnDestroy {

    private destroyed = new Subject<void>();
    private hierarchySubj: ReplaySubject<HierarchyWithIdMap>;
    private selectionSubj: BehaviorSubject<HierarchyEntity>;

    constructor(private readonly apiService: SmagApiService,
                private readonly loginService: LoginService,
                private readonly router: Router,
                private readonly route: ActivatedRoute) {
        this.hierarchySubj = new ReplaySubject<HierarchyWithIdMap>(1); // like BehaviorSubject but with no need for initial value
        this.selectionSubj = new BehaviorSubject<HierarchyEntity>(null);
        // reload is triggered every time a user gets logged-in (different principals may have different permissions)
        loginService.addSetupHook(0, profile => {
            console.debug(`Retrieving hierarchy for user ${profile?.username}`);
            return this.reloadHierarchy();
        });
        // attach selection to URL
        // NOTE: hierarchy reloading automatically triggers emission on entity selection observable,
        //       due to findEntityById() depending on hierarchySubj.
        //       This behaviour covers both the case when id in url does not change and when it changes.
        //       If id in url changes *and* hierarchy is reloaded, then two subsequent emissions on selectionSubj
        //       are to be expected; these may cause double REST calls to e.g. /config/hierarchy/<id> if there are
        //       subscribers to current selection.
        this.route.queryParamMap.pipe(
            map(params => params.get('id')),
            switchMap(id => id ? this.findEntityById(id) : of(null)),
            takeUntil(this.destroyed),
        ).subscribe(entity => this.selectionSubj.next(entity));
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get hierarchy(): Observable<Hierarchy> {
        return this.hierarchySubj.pipe(map(h => h.hierarchy));
    }

    get hierarchyIds(): Observable<{ [entityId: string]: HierarchyEntity }> {
        return this.hierarchySubj.pipe(map(h => h.idMap));
    }

    get selection(): Observable<HierarchyEntity> {
        return this.selectionSubj.asObservable();
    }

    findEntityByPath(path: string): Observable<HierarchyEntity> {
        return this.hierarchy.pipe(map(h => resolveEntity(path, h)));
    }

    findEntityById(id: string): Observable<HierarchyEntity> {
        return this.hierarchySubj.pipe(map(h => h.idMap[id]));
    }

    findFirstEntity(predicate: (entity: HierarchyEntity) => boolean): Observable<HierarchyEntity> {
        return this.hierarchy.pipe(map(h => findFirst(predicate, h)));
    }

    findAllEntities(predicate: (entity: HierarchyEntity) => boolean): Observable<HierarchyEntity[]> {
        return this.hierarchy.pipe(map(h => findAll(predicate, h)));
    }

    select(id?: string) {
        this.router.navigate([], {
            queryParams: {id: id || undefined},
            queryParamsHandling: 'merge'
        });
    }

    reloadHierarchy(): Promise<HierarchyWithIdMap> {
        return this.apiService.getHierarchy().toPromise()
            .then(h => {
                const hierarchy = mkHierarchy(h);
                this.hierarchySubj.next(hierarchy);
                return hierarchy;
            });
    }
}

function resolveEntity(path: string, hierarchy: Hierarchy): HierarchyEntity {
    // decompose path
    const names = splitPath(path);
    // check for empty device path
    if (!names.length) {
        return null;
    }
    // traverse the entity-tree starting from the root
    // at each step, look for a child device with the current name
    let entity: HierarchyEntity = null;
    for (const name of names) {
        let found = false;
        for (const child of (entity ? (entity.children || []) : (hierarchy.entities || []))) {
            if (child.name === name) {
                entity = child;
                found = true;
                break;
            }
        }
        if (!found) {
            return null;
        }
    }
    return entity;
}

function findFirst(predicate: (entity: HierarchyEntity) => boolean, hierarchy: Hierarchy): HierarchyEntity {
    function _find(node: HierarchyEntity) {
        if (predicate(node)) {
            return node;
        }
        for (let child of (node.children || [])) {
            const node = _find(child);
            if (node) {
                return node;
            }
        }
        return null;
    }

    for (let root of (hierarchy.entities || [])) {
        const node = _find(root);
        if (node) {
            return node;
        }
    }
    return null;
}

function findAll(predicate: (entity: HierarchyEntity) => boolean, hierarchy: Hierarchy): HierarchyEntity[] {
    const nodes = [];

    function _find(node: HierarchyEntity) {
        if (predicate(node)) {
            nodes.push(node);
        }
        for (let child of (node.children || [])) {
            _find(child);
        }
    }

    for (let root of (hierarchy.entities || [])) {
        _find(root);
    }
    return nodes;
}

function splitPath(path: string): string[] {
    if (!path) {
        return [];
    }
    return path.split('/');
}

function mkHierarchy(hierarchy: AuthorizedObject<SmagHierarchy>): HierarchyWithIdMap {
    const idMap: { [entityId: string]: HierarchyEntity } = {};

    function mkEntity(entity: AuthorizedObject<SmagHierarchyEntity>, parent: HierarchyEntity): HierarchyEntity {
        const entityWithParent: HierarchyEntity = {
            ...entity,
            parent,
            path: `${parent?.path ? parent.path + '/' : ''}${entity.name}`,
            children: undefined // set later
        };
        entityWithParent.children = entity.children ? entity.children.map(child => mkEntity(child, entityWithParent)) : [];
        idMap[entityWithParent.id] = entityWithParent;
        return entityWithParent;
    }

    const hierarchyWithParents = {
        entities: hierarchy.entities ? hierarchy.entities.map(entity => mkEntity(entity, null)) : [],
        root: hierarchy.root,
        permissions: hierarchy.permissions
    };

    return {
        hierarchy: hierarchyWithParents,
        idMap
    };
}
